import { Building2, Map } from 'lucide-react'
import { type Control, Controller, type FieldErrors } from 'react-hook-form'

import { MultiSelectAutocomplete, type SelectOption } from '@/components/ui/MultiSelectAutocomplete'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'

type LocationGroupFieldName = 'dbas' | 'districts'

interface LocationGroupFieldProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  errors: FieldErrors<UserFormInput>
  fieldName: LocationGroupFieldName
  label: string
  icon: 'building' | 'map'
  options: SelectOption[]
  isRootOperator: boolean
  isSysAdmin: boolean
  hasMultipleSystems: boolean
  selectedSystem: string | null
  isLoadingLocationData: boolean
  placeholder: string
}

const iconMap = {
  building: Building2,
  map: Map,
}

const descriptionMap: Record<LocationGroupFieldName, string> = {
  dbas: 'DBA entities this user can manage',
  districts: 'Districts this user can access',
}

const FieldDescription = ({
  fieldName,
  selectedSystem,
  errors,
}: {
  fieldName: LocationGroupFieldName
  selectedSystem: string | null
  errors: FieldErrors<UserFormInput>
}) => {
  // eslint-disable-next-line security/detect-object-injection
  const description = descriptionMap[fieldName]
  // eslint-disable-next-line security/detect-object-injection
  const fieldError = errors.attributes?.[fieldName]

  return (
    <>
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        {description} {selectedSystem && `(for ${selectedSystem})`}
      </p>
      {fieldError && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{fieldError.message}</p>
      )}
    </>
  )
}

export const LocationGroupField = ({
  control,
  errors,
  fieldName,
  label,
  icon,
  options,
  isRootOperator,
  isSysAdmin,
  hasMultipleSystems,
  selectedSystem,
  isLoadingLocationData,
  placeholder,
}: LocationGroupFieldProps) => {
  // eslint-disable-next-line security/detect-object-injection
  const IconComponent = iconMap[icon]
  const valueFieldName = `attributes.${fieldName}` as const
  const isHighLevelRole = isRootOperator || isSysAdmin

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-400">
          <IconComponent className="w-4 h-4" />
        </span>
        <label
          htmlFor={valueFieldName}
          className={`text-sm font-medium ${isHighLevelRole ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}
        >
          {label}
        </label>
      </div>

      <div>
        <Controller
          name={valueFieldName}
          control={control}
          render={({ field }) => {
            const value = Array.isArray(field.value) ? field.value : []
            const isDisabled = !selectedSystem || hasMultipleSystems || isHighLevelRole

            return (
              <MultiSelectAutocomplete
                options={options}
                value={value}
                onChange={(newValue) => {
                  if (!hasMultipleSystems && !isHighLevelRole) {
                    field.onChange(newValue)
                  }
                }}
                placeholder={placeholder}
                disabled={isDisabled}
                isLoading={isLoadingLocationData && !!selectedSystem}
              />
            )
          }}
        />
        <FieldDescription fieldName={fieldName} selectedSystem={selectedSystem} errors={errors} />
      </div>
    </div>
  )
}
